import { GetPaymentResultDTO } from '../@types/GetPaymentResultDTO'
import { apiClient } from '@src/services/http-common'
import { useFetchPerson } from '../person-api'
import { Person } from '../@types/GetPersonDTO'
import { useQuery } from '@tanstack/react-query'

interface GetPaymentResult {
  closestPayment: GetPaymentResultDTO | null
  lastPayment: GetPaymentResultDTO | null
  loanIdToUse: string
  closedNextPayment: boolean
}

const SCOPE = 'list'
const ENTITY = 'payments'

const KeyFactory = {
  payments: (loanId: string) => [SCOPE, ENTITY, loanId],
}

export const getPayment = async (loanId: string): Promise<GetPaymentResultDTO[]> => {
  const response = await apiClient.get<GetPaymentResultDTO[]>(`/Loans/${loanId}/payments`)
  return response.data
}

const findFirstLoanId = (personData: Person | null) => {
  if (!personData?.loans?.length) {
    return { isClosed: false, firstLoanId: '' };
  }
  const firstLoan = personData.loans[0];
  const firstLoanId = firstLoan ? String(firstLoan.id) : '';
  const isClosed = firstLoan ? firstLoan.isClosed : false;

  return { isClosed, firstLoanId };
}


export const findClosestPayment = async (
  loanId: string | null = null,
  personData: Person | null
): Promise<GetPaymentResult | null> => {
  const today = new Date().toISOString();
  const loanToFetch = loanId ?? findFirstLoanId(personData).firstLoanId;
  const payments = loanToFetch ? await getPayment(loanToFetch) : [];
  const filteredPayments = payments.filter((payment) => new Date(payment.plannedOnDateUTC) > new Date(today));
  const pastPayments = payments.filter((payment) => new Date(payment.plannedOnDateUTC) <= new Date(today));
  const closestPayment = filteredPayments.length ? filteredPayments[0] : null;
  const lastPayment = pastPayments.length ? pastPayments[pastPayments.length - 1] : null;

  const loanIdToUse = closestPayment ? loanToFetch : findFirstLoanId(personData).firstLoanId;
  const closedNextPayment = closestPayment ? Boolean(loanToFetch) : findFirstLoanId(personData).isClosed;

  return closestPayment ? { closestPayment, lastPayment, loanIdToUse, closedNextPayment } : null;
};

export const usePayment = (loanId?: string): [GetPaymentResult | null | undefined, boolean] => {
  const [personData, isLoadingPerson] = useFetchPerson();

  const queryFn = () => personData ? findClosestPayment(loanId, personData) : Promise.resolve(null);

  const { data, isLoading: isLoadingPayments } = useQuery<GetPaymentResult | null>({
    queryKey: KeyFactory.payments(loanId ?? 'default'),
    queryFn,
    enabled: !isLoadingPerson && !!personData, 
  });

  return [data, isLoadingPerson || isLoadingPayments];
};